import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useSetPaymentIntentId } from "@babackapp/return-widget-components/hooks/use-set-payment-intent-id";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useCreatePaymentIntent } from "@babackapp/return-widget-components/mutations/use-create-payment-intent";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import type {
  Stripe,
  StripeElements,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import config from "@/config";

type StripeInstance = { stripe: Stripe; elements: StripeElements };

export type UseStripeCheckoutReturn = {
  stripePromise: Promise<Stripe | null>;
  elementsOptions: StripeElementsOptions;
  onReady: (stripe: Stripe, elements: StripeElements) => void;
  onLoadError: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isReady: boolean;
  errorMessage: string | null;
};

function mapLocaleToStripe(lang: string | undefined): string {
  if (!lang) return "auto";
  const lower = lang.toLowerCase();
  if (lower === "no") return "nb";
  if (lower === "pt-pt") return "pt";
  if (lower === "zh-hans") return "zh";
  if (lower === "zh-hant") return "zh-TW";
  return lower;
}

export function useStripeCheckout(
  orderId: string,
  shopId: string,
  checkoutToken: string | null,
): UseStripeCheckoutReturn {
  const { data: shop } = usePublicShop(shopId);
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setPaymentIntentId = useSetPaymentIntentId(orderId);
  const createPaymentIntent = useCreatePaymentIntent();
  const navigate = useNavigate();
  const t = useCustomText();
  const { i18n } = useTranslation("return-widget-v3");
  const stripeLocale = mapLocaleToStripe(i18n.language);

  const [stripeInstance, setStripeInstance] = useState<StripeInstance | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const stripeAccountRef = useRef<string | null | undefined>(undefined);
  const stripeLocaleRef = useRef<string | null>(null);
  const stripePromiseRef = useRef<Promise<Stripe | null> | null>(null);
  const stripeAccount = shop?.stripeAccount ?? undefined;
  if (
    stripeAccountRef.current !== stripeAccount ||
    stripeLocaleRef.current !== stripeLocale ||
    !stripePromiseRef.current
  ) {
    stripeAccountRef.current = stripeAccount;
    stripeLocaleRef.current = stripeLocale;
    stripePromiseRef.current = loadStripe(config.stripePublicKey, {
      stripeAccount,
      locale: stripeLocale as Parameters<typeof loadStripe>[1]["locale"],
    });
  }

  const elementsOptions = useMemo<StripeElementsOptions>(
    () => ({
      mode: "payment",
      amount: Math.round(financialSummary.amountDue),
      currency: financialSummary.currency.toLowerCase(),
      payment_method_types: ["card"],
    }),
    [financialSummary.amountDue, financialSummary.currency],
  );

  const onReady = useCallback((stripe: Stripe, elements: StripeElements) => {
    setStripeInstance({ stripe, elements });
  }, []);

  const onLoadError = useCallback(() => {
    setErrorMessage(t("stripe.error.loadForm"));
  }, [t]);

  const onSubmit = useCallback(async () => {
    if (!stripeInstance || !draft || !checkoutToken) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const { error: submitError } = await stripeInstance.elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message ?? t("stripe.error.validation"));
        return;
      }

      const { client_secret } = await createPaymentIntent.mutateAsync({
        draft,
        checkoutToken,
      });
      if (!client_secret) throw new Error("No client secret returned");

      const { error: paymentError, paymentIntent } =
        await stripeInstance.stripe.confirmPayment({
          elements: stripeInstance.elements,
          clientSecret: client_secret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name:
                  `${draft.address?.first_name ?? ""} ${draft.address?.last_name ?? ""}`.trim() ||
                  undefined,
                email: draft.address?.email ?? undefined,
              },
            },
          },
          redirect: "if_required",
        });

      if (paymentError) {
        setErrorMessage(paymentError.message ?? t("stripe.error.payment"));
        return;
      }

      if (paymentIntent?.id) {
        setPaymentIntentId(paymentIntent.id);
      }
      navigate("../complete");
    } catch (e) {
      setErrorMessage((e as Error).message ?? t("stripe.error.generic"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    stripeInstance,
    draft,
    checkoutToken,
    createPaymentIntent,
    setPaymentIntentId,
    navigate,
    t,
  ]);

  return {
    stripePromise: stripePromiseRef.current!,
    elementsOptions,
    onReady,
    onLoadError,
    onSubmit,
    isSubmitting: isSubmitting || createPaymentIntent.isPending,
    isReady: !!stripeInstance,
    errorMessage,
  };
}
